/**
 * Browser-zone independent time keys for events.
 *
 * Event dates are stored as zoneless 'yyyy-MM-ddTHH:mm:ss' strings (UTC and port local time). The Date fields on `EventDate` are
 * parsed in the browser zone, so a time that falls inside the browser's DST gap or overlap is shifted or ambiguous.
 *
 * One explicit base is used for every key: "time milliseconds", i.e. `Date.UTC(y, m, d, h, mi, s)` of the stored time, read as
 * UTC fields (not the browser-local Date).
 *  - UTC keys:   the stored UTC string as a UTC instant (the Z string and the same stored time give the same number).
 *  - Local keys: the stored port local time, DST-free.
 * A key is never a browser-epoch `getTime()`.
 *
 * The exact local time of a Date is remembered in a registry that is keyed by the Date object itself and valid only while
 * the Date still holds the minute it was registered for (the ingestion transform in `eventSortPipe.ts` clears the seconds
 * in place, which keeps the key valid). A replaced Date (a different object) or a Date edited to another minute has no valid
 * registration, so its key is recomputed from its own fields. Nothing is ever compared by materialising a Date again, which is
 * ambiguous inside a DST gap (02:30 and 03:30 give the same Date in Europe/Oslo).
 * Ordering, day assignment and elapsed time must use these keys, never the browser-zone Date.
 */

const TIME_ZONE_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?)?/;
const MS_PER_MINUTE = 60 * 1000;

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const fieldsToTimeMs = (date) =>
    Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );

/** Browser-local Date whose fields hold the time encoded in `timeMs` (shifted by the browser if the time is in its DST gap). */
const materialize = (timeMs) => {
    const d = new Date(timeMs);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds());
};

const minuteOf = (date) => Math.floor(date.getTime() / MS_PER_MINUTE);

/** Epoch ms of the local time, with the seconds and milliseconds removed. */
export const floorTimeMsToMinute = (timeMs) => (isFiniteNumber(timeMs) ? Math.floor(timeMs / MS_PER_MINUTE) * MS_PER_MINUTE : null);

// Date object -> { timeMs, minute }. `minute` is the minute the Date held when the local time was registered.
const registeredLocalTimes = new WeakMap();

/** Remembers the exact local time (epoch ms of Date.UTC fields) of a Date that the browser may have shifted. Returns the Date. */
export const registerLocalTime = (date, timeMs) => {
    if (date instanceof Date && !Number.isNaN(date.getTime()) && isFiniteNumber(timeMs)) {
        registeredLocalTimes.set(date, { timeMs, minute: minuteOf(date) });
    }
    return date;
};

/** Browser-local Date for a local time; remembers the exact local time so it survives a DST gap until the Date is replaced. */
export const timeMsToLocalDate = (timeMs) => registerLocalTime(materialize(timeMs), timeMs);

/** The exact local time registered for this Date object, or null when it has none or it was edited to another minute. */
export const getExactTimeMs = (date) => {
    if (!(date instanceof Date)) return null;
    const entry = registeredLocalTimes.get(date);
    return entry && entry.minute === minuteOf(date) ? entry.timeMs : null;
};

/** Local time held by a Date: the registered exact value when still valid, otherwise its browser-local fields read as UTC. */
export const dateToTimeMs = (date) => {
    const exact = getExactTimeMs(date);
    return exact !== null ? exact : fieldsToTimeMs(date);
};

/** Local time of a 'yyyy-MM-ddTHH:mm[:ss]' string (a trailing Z or offset is ignored), or of a Date (see dateToTimeMs). */
export const parseLocalTimeMs = (value) => {
    if (value instanceof Date) {
        const ms = dateToTimeMs(value);
        return Number.isNaN(ms) ? null : ms;
    }
    if (typeof value !== 'string') return null;
    const match = LOCAL_TIME_PATTERN.exec(value.trim());
    if (!match) return null;
    const [, y, mo, d, h, mi, s, ms] = match;
    return Date.UTC(+y, +mo - 1, +d, +(h ?? 0), +(mi ?? 0), +(s ?? 0), ms ? +ms.padEnd(3, '0') : 0);
};

/** Like parseLocalTimeMs for a string, but null when the string carries a zone designator (Z or an offset), so it is not a plain local time. */
export const parseZonelessLocalTimeMs = (value) =>
    typeof value === 'string' && !TIME_ZONE_SUFFIX.test(value.trim()) ? parseLocalTimeMs(value) : null;

/**
 * UTC instant (epoch ms) of a stored UTC string. A string without a zone designator is read as UTC, never as browser-local.
 * A Date is read through its stored time (see dateToTimeMs), because UTC Dates in the app hold the UTC time in their fields.
 */
export const parseUtcInstantMs = (value) => {
    if (value instanceof Date) return parseLocalTimeMs(value);
    if (typeof value !== 'string' || value.length === 0) return null;
    if (TIME_ZONE_SUFFIX.test(value.trim())) {
        const ms = Date.parse(value.trim());
        return Number.isNaN(ms) ? null : ms;
    }
    return parseLocalTimeMs(value);
};

/** UTC key of the event's actual UTC time (epoch ms, UTC base), or null. Never a browser-epoch value. */
export const getEventUtcMs = (event) => {
    const utc = event?.eventDate?.actualDateTimeUtc;
    return utc ? parseUtcInstantMs(utc) : null;
};

/** Port local time of the event's actual local time as epoch ms (DST-free, UTC fields), or null. */
export const getEventLocalTimeMs = (event) => {
    const local = event?.eventDate?.actualDateTimeLocal;
    return local ? parseLocalTimeMs(local) : null;
};
